/**
 * Small motion helpers of the public onboarding page, kept calm on purpose: only `opacity` and
 * `transform` move, height changes go through the grid-rows pattern (`0fr` to `1fr`), durations stay
 * between 200 and 500 ms and the curve eases out.
 *
 * Reduced motion is read here (not from a library cache) so that every helper and the file list
 * agree, and so a test can switch it with a `matchMedia` mock.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { motion } from "motion/react";

export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
/** The same curve for motion's `transition.ease`. */
export const EASE_ARRAY = [0.22, 1, 0.36, 1] as const;
export const COLLAPSE_MS = 380;

const QUERY = "(prefers-reduced-motion: reduce)";

export function usePrefersReducedMotion(): boolean {
  const read = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(QUERY).matches : false);
  const [reduced, setReduced] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(QUERY);
    const listener = () => setReduced(media.matches);
    listener();
    media.addEventListener?.("change", listener);
    return () => media.removeEventListener?.("change", listener);
  }, []);
  return reduced;
}

/**
 * Fades (and, unless reduced, lifts) its children in the first time they come into view. Without
 * `IntersectionObserver` they are simply shown. Only transform and opacity change, so nothing around
 * them moves.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(() => typeof IntersectionObserver === "undefined");

  useEffect(() => {
    if (shown) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);

  const style: CSSProperties = {
    opacity: shown ? 1 : 0,
    transform: shown || reduced ? "none" : "translateY(16px)",
    transition: `opacity 460ms ${EASE} ${delay}ms, transform 460ms ${EASE} ${delay}ms`,
  };
  return (
    <div ref={ref} className={className} style={style} data-revealed={shown ? "true" : "false"}>
      {children}
    </div>
  );
}

/**
 * Height through the grid-rows pattern. The children stay mounted; `open` only changes the row
 * track (`0fr` to `1fr`) and the opacity. Hidden content is not focusable (`visibility`) and not read
 * out (`aria-hidden`).
 */
export function Collapse({
  open,
  children,
  className,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const parts: string[] = [];
  if (!reduced) parts.push(`grid-template-rows ${COLLAPSE_MS}ms ${EASE}`);
  parts.push(`opacity 300ms ${EASE}`, `visibility 0s linear ${open ? 0 : COLLAPSE_MS}ms`);
  const style: CSSProperties = {
    display: "grid",
    gridTemplateRows: open ? "1fr" : "0fr",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transition: parts.join(", "),
  };
  return (
    <div className={className} style={style} aria-hidden={open ? undefined : true} data-open={open ? "true" : "false"}>
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * A block that is mounted while `show` is true and, after `show` turns false, folds away and is
 * only then removed. Present at the first render it is simply there (no entrance on a reload).
 * `children` may be a function of the last value so the content stays while it folds away.
 */
export function CollapsePresence({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(show);
  const [open, setOpen] = useState(show);
  const lastChildren = useRef(children);
  if (show) lastChildren.current = children;

  useEffect(() => {
    if (show) {
      setMounted(true);
      // one frame in the folded state, so the opening has something to transition from
      const frame = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(frame);
    }
    setOpen(false);
    const timer = setTimeout(() => setMounted(false), reduced ? 0 : COLLAPSE_MS + 40);
    return () => clearTimeout(timer);
  }, [show, reduced]);

  if (!mounted) return null;
  return (
    <Collapse open={open && show} className={className}>
      {show ? children : lastChildren.current}
    </Collapse>
  );
}

/** Data saver: the staging stays quiet then, too. */
export const prefersLessData = (): boolean => {
  const connection = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
  return !!connection?.saveData || (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-data: reduce)").matches);
};

/**
 * Whether the staging (intro, title cards, the growing house, the figures' tours) plays: always, except for readers who
 * prefer reduced motion or save data. It is no switch and nothing is remembered.
 */
export function useStaging(): boolean {
  const reduced = usePrefersReducedMotion();
  return !reduced && !prefersLessData();
}

/** Things shown once per browser session (the intro, each title card), remembered per link. */
export function sessionFlag(key: string): { seen: () => boolean; mark: () => void } {
  return {
    seen: () => {
      try {
        return window.sessionStorage.getItem(key) === "1";
      } catch {
        return false;
      }
    },
    mark: () => {
      try {
        window.sessionStorage.setItem(key, "1");
      } catch {
        // ignore
      }
    },
  };
}


/**
 * A box that takes the height of its content and glides to a new one when the content changes (a
 * step with more text, a list that gets longer). The height is measured, never fixed: nothing is
 * cut off - `overflow` is only clipped for the moment of the glide, then visible again.
 */
export function GrowBox({ children, className }: { children: ReactNode; className?: string }) {
  const reduced = usePrefersReducedMotion();
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");
  const [animating, setAnimating] = useState(false);
  const first = useRef(true);

  useLayoutEffect(() => {
    const node = inner.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const measured = Math.ceil(entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight);
      if (first.current) {
        first.current = false;
        setHeight(measured);
        return;
      }
      setHeight((previous) => {
        if (previous !== measured) setAnimating(!reduced);
        return measured;
      });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <motion.div
      className={className}
      initial={false}
      animate={{ height }}
      transition={reduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      onAnimationComplete={() => setAnimating(false)}
      style={{ overflow: animating ? "hidden" : "visible" }}
      data-testid="grow-box"
    >
      <div ref={inner}>{children}</div>
    </motion.div>
  );
}
